import { useEffect, useMemo, useState } from 'react';

import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Empty,
  Input,
  Popover,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useEvidenceModal } from '../hooks/useEvidenceModal';
import { TurnCheckInStatusAttendanceRowData } from '../types';
import { CHECKIN_STATUS_COLORS, CheckinStatusKey } from '../utils';
import EvidenceModal from './EvidenceModal';

const BASE = 'scenes.kit.dashboard_alternative_mode.components.dashboard_grid';

const { Text } = Typography;

const ALL_STATUSES = '__all__';

function normalize(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

interface DashboardGridProps {
  data?: TurnCheckInStatusAttendanceRowData[];
  loading?: boolean;
  touchLatamApiUrl: string;
}

function DashboardGrid({
  data,
  loading,
  touchLatamApiUrl,
}: DashboardGridProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const dateFormat: string = useSelector((state: any) => state.intl.dateFormat);

  const evidenceModal = useEvidenceModal({ message });
  const [currentPage, setCurrentPage] = useState(1);

  const [filterOpen, setFilterOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);

  // Resetear filtros y página cada vez que llegan nuevos datos del padre
  useEffect(() => {
    setCurrentPage(1);
    setSearchText('');
    setStatusFilter(ALL_STATUSES);
    setFilterOpen(false);
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    const needle = normalize(searchText.trim());
    return data.filter((row) => {
      const matchesSearch =
        needle === '' ||
        normalize(row.task.name).includes(needle) ||
        normalize(row.store.name).includes(needle) ||
        normalize(`${row.user.name} ${row.user.lastname}`).includes(needle);

      const matchesStatus =
        statusFilter === ALL_STATUSES || row.checkinStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchText, statusFilter]);

  const hasActiveFilters =
    searchText.trim() !== '' || statusFilter !== ALL_STATUSES;

  const statusOptions = [
    { value: ALL_STATUSES, label: t(`${BASE}.filter.status_all`) },
    ...(['on_time', 'late', 'absent', 'pending'] as CheckinStatusKey[]).map(
      (key) => ({
        value: key,
        label: t(`${BASE}.table.checkin_status.${key}`),
      })
    ),
  ];

  const filterContent = (
    <Space direction="vertical" size={12} style={{ width: 240 }}>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(`${BASE}.filter.search_label`)}
        </Text>
        <Input
          placeholder={t(`${BASE}.filter.search_placeholder`)}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          allowClear
          style={{ marginTop: 4 }}
        />
      </div>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(`${BASE}.filter.status_label`)}
        </Text>
        <Select
          value={statusFilter}
          options={statusOptions}
          onChange={(val) => {
            setStatusFilter(val);
            setCurrentPage(1);
          }}
          style={{ width: '100%', marginTop: 4 }}
        />
      </div>
      {hasActiveFilters && (
        <Button
          size="small"
          onClick={() => {
            setSearchText('');
            setStatusFilter(ALL_STATUSES);
            setCurrentPage(1);
          }}
        >
          {t(`${BASE}.filter.clear`)}
        </Button>
      )}
    </Space>
  );

  const columns: ColumnsType<TurnCheckInStatusAttendanceRowData> = [
    {
      title: t(`${BASE}.table.columns.date`),
      key: 'date',
      width: 110,
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => (
        <Text>{dayjs(row.turn.startDate).format(dateFormat)}</Text>
      ),
    },
    {
      title: t(`${BASE}.table.columns.campaign`),
      key: 'campaign',
      ellipsis: true,
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => (
        <Text>{row.task.name}</Text>
      ),
    },
    {
      title: t(`${BASE}.table.columns.store`),
      key: 'store',
      ellipsis: true,
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => (
        <Text>{row.store.name}</Text>
      ),
    },
    {
      title: t(`${BASE}.table.columns.capturer`),
      key: 'capturer',
      ellipsis: true,
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => (
        <Text>{`${row.user.name} ${row.user.lastname}`}</Text>
      ),
    },
    {
      title: t(`${BASE}.table.columns.schedule`),
      key: 'schedule',
      width: 130,
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => (
        <Text>
          {dayjs(row.turn.startDate).format('HH:mm')}
          {' - '}
          {dayjs(row.turn.endDate).format('HH:mm')}
        </Text>
      ),
    },
    {
      title: t(`${BASE}.table.columns.checkin_status`),
      key: 'checkinStatus',
      width: 140,
      align: 'center',
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => {
        const statusKey = row.checkinStatus as CheckinStatusKey;
        const colors = CHECKIN_STATUS_COLORS[statusKey];
        return (
          <Tag
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
              borderColor: colors.bg,
              fontWeight: 500,
              margin: 0,
            }}
          >
            {t(`${BASE}.table.checkin_status.${statusKey}`)}
          </Tag>
        );
      },
    },
    {
      title: t(`${BASE}.table.columns.evidence`),
      key: 'evidence',
      width: 90,
      align: 'center',
      render: (_: unknown, row: TurnCheckInStatusAttendanceRowData) => (
        <Button
          type="text"
          icon={<SearchOutlined />}
          onClick={() => evidenceModal.openModal(row.userId, row.turnId)}
        />
      ),
    },
  ];

  const filterButton = (
    <Popover
      content={filterContent}
      trigger="click"
      open={filterOpen}
      onOpenChange={setFilterOpen}
      placement="bottomRight"
    >
      <Button
        icon={<FilterOutlined />}
        type={hasActiveFilters ? 'primary' : 'default'}
      >
        {t(`${BASE}.filter.button`)}
      </Button>
    </Popover>
  );

  return (
    <>
      <Card title={t(`${BASE}.title`)} extra={filterButton}>
        <Table<TurnCheckInStatusAttendanceRowData>
          columns={columns}
          dataSource={filteredData}
          rowKey={(row) => `${row.userId}-${row.turnId}`}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: 15,
            showSizeChanger: false,
            showTotal: (total) =>
              t(`${BASE}.table.pagination.total`, { count: total }),
            onChange: (page) => setCurrentPage(page),
          }}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span style={{ color: '#d9d9d9' }}>{t(`${BASE}.empty`)}</span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          size="middle"
        />
      </Card>

      <EvidenceModal
        open={evidenceModal.open}
        loading={evidenceModal.loading}
        detail={evidenceModal.detail}
        onClose={evidenceModal.closeModal}
        touchLatamApiUrl={touchLatamApiUrl}
      />
    </>
  );
}

export default DashboardGrid;
